/**
 * Collab session glue (Collab Phase 8) — the shell's facade over the protocol
 * client. Owns the single CollabClient instance, the connect gate, the
 * divergence "Join session" dialog, room-snapshot adoption, save-notify
 * adoption, and the status getters the shell's cloud-sync tick reads.
 *
 * Gate (Phase 8, dev-flag scoped): a real signed-in user + a writable tab +
 * a cloud project + (localStorage `pb-collab-dev` OR the project is actually
 * shared). Phase 11 replaces the dev flag with membership-driven auto-connect.
 */

import { identityRepairNamespaceOf } from '../shared/editable-id-repair.js';
import { createCollabClient } from './collab-client.js';
import { store, WRITE_BLOCKER } from '../core/state.js';
import { eventBus } from '../core/events.js';
import { getTemplateApiOrigin, getAuthBearerToken } from '../templates/template-api.js';
import { isRealUser, listProjectMembers } from '../templates/auth-api.js';
import { ensureAllPagesHydrated } from '../sections/section-manager.js';
import { applyLoadedProjectState, setCloudVersionAndPersist } from '../core/save-load.js';
import { createModal, createButton } from '../ui/components.js';
import { showToast } from '../ui/toast.js';
import { reportError } from '../core/report-error.js';
import { getProfile } from '../core/profile-store.js';
import { recordSessionBoundaryMetric } from '../core/session-boundary-observability.js';
import {
  installCollabLocks,
  uninstallCollabLocks,
  updateLockState,
  peerLockTreatmentRows
} from './collab-locks.js';

let client = null;
let peerCount = 0;
let connectAttempt = 0;
// Phase 11 save-leader stall fallback: last time a save actually happened in the
// room (a save-notify, or our own connect). If the elected leader goes silent
// past the threshold while we're dirty, a non-leader self-promotes for one tick.
let lastSaveActivityAt = 0;
let claimModal = null;
const SAVE_LEADER_STALL_MS = 5 * 60_000;
let hooks = {
  // Snapshot the local pre-join state to the revision store so "Join session"
  // is recoverable. Set by the shell (it owns the RevisionStore).
  snapshotBeforeAdopt: async () => {}
};

function devFlag() {
  try {
    return globalThis.localStorage?.getItem('pb-collab-dev') === '1';
  } catch {
    return false;
  }
}

/**
 * SB-4a. True when the room is open to EVERY cloud project, not just shared
 * ones. Read from the profile the client already fetches at boot; the server
 * enforces the same flag at the upgrade, so this is a round-trip saver and not
 * the security boundary.
 *
 * Absent/unloaded profile ⇒ false ⇒ we fall through to the membership check
 * below, which is the pre-SB-4a behavior. That is the correct direction: a
 * missing flag must never *widen* access.
 */
function collabAllProjectsEnabled() {
  return getProfile()?.features?.collabAllProjects === true;
}

/**
 * Pre-SB-4a gate, still used while COLLAB_ALL_PROJECTS_ENABLED is off.
 *
 * C3: this used to `catch { return false }`, so a transient network error
 * silently downgraded a genuinely shared project to NO coordination — the user
 * got two uncoordinated writers with nothing said. It still returns false (not
 * connecting IS the closed direction), but the failure is now REPORTED rather
 * than swallowed, so "collab quietly didn't start" stops being invisible.
 * When the flag is on this predicate is not consulted at all.
 */
async function isProjectShared(currentUser, projectId) {
  if (!projectId) return false;
  try {
    const resp = await listProjectMembers(projectId);
    const members = Array.isArray(resp?.members) ? resp.members : [];
    const selfIsMember = members.some(
      (m) => m.sub === String(currentUser.sub) && m.role !== 'owner'
    );
    return members.length > 1 || selfIsMember;
  } catch (err) {
    reportError('Collab gate', err, {
      user: 'silent',
      detail: `membership check failed for ${projectId}; live editing did not start`,
      id: 'collab-gate-membership'
    });
    return false;
  }
}

// ── divergence dialog ──
function showDivergenceModal() {
  return new Promise((resolve) => {
    let settled = false;
    const finish = (value) => {
      if (settled) return;
      settled = true;
      modal.close();
      resolve(value);
    };
    const modal = createModal({
      title: 'Join live session',
      size: 'sm',
      showClose: false,
      dismissOnBackdrop: false,
      onClose: () => finish('alone')
    });
    const p = document.createElement('p');
    p.className = 'pb-collab-divergence-text';
    p.textContent =
      'This project has an active editing session with different content. ' +
      'Join to load the shared copy, or keep working alone on your version.';
    modal.body.appendChild(p);
    modal.footer.appendChild(
      createButton({
        variant: 'ghost',
        label: 'Keep working alone',
        onClick: () => finish('alone')
      })
    );
    modal.footer.appendChild(
      createButton({
        variant: 'primary',
        label: 'Join session',
        onClick: () => finish('adopt')
      })
    );
  });
}

function showEditorClaimModal(editorClaim) {
  if (claimModal) return;
  recordSessionBoundaryMetric('takeover_prompt_shown');
  const label = editorClaim?.deviceLabel || editorClaim?.holderEmail || 'another window';
  const modal = createModal({
    title: 'Already editing elsewhere',
    size: 'sm',
    showClose: false,
    dismissOnBackdrop: false
  });
  claimModal = modal;
  const p = document.createElement('p');
  p.className = 'pb-collab-divergence-text';
  p.textContent = `This project is being edited in ${label}. Open read-only here, or take over editing.`;
  modal.body.appendChild(p);
  modal.footer.append(
    createButton({
      variant: 'ghost',
      label: 'Open read-only',
      onClick: () => {
        recordSessionBoundaryMetric('takeover_prompt_readonly');
        modal.close();
        claimModal = null;
      }
    }),
    createButton({
      variant: 'primary',
      label: 'Take over editing',
      onClick: () => {
        recordSessionBoundaryMetric('takeover_prompt_accepted');
        client?.claimEditor('takeover');
        modal.close();
        claimModal = null;
      }
    })
  );
}

async function adoptSnapshot(state) {
  try {
    await hooks.snapshotBeforeAdopt();
  } catch (err) {
    console.warn('[Collab] pre-adopt snapshot failed:', err?.message || err);
  }
  await applyLoadedProjectState(state, { label: 'collab join' });
  eventBus.emit('project:restored', { projectId: state?.projectId });
}

function handleSaveNotify({ version, atSeq }) {
  lastSaveActivityAt = Date.now();
  const projectId = store.getState()?.projectId;
  if (!projectId || !Number.isInteger(version)) return;
  void setCloudVersionAndPersist(projectId, version);
  eventBus.emit('collab:save-adopted', { version, atSeq });
}

function handleOpDropped(op, info) {
  if (info?.reason === 'lock_held') {
    const who = info.holder?.email || 'Someone';
    showToast(`${who} is editing that section — your change was undone`, {
      type: 'info',
      id: 'collab-lock-held',
      duration: 6000
    });
  }
}

function ensureClient() {
  if (client) return client;
  client = createCollabClient({
    store,
    eventBus,
    getOrigin: getTemplateApiOrigin,
    getToken: getAuthBearerToken,
    ensureHydrated: ensureAllPagesHydrated,
    onDivergence: showDivergenceModal,
    onAdoptSnapshot: adoptSnapshot,
    onFatalError: (err, info = {}) =>
      reportError('Collab session', err, {
        user: 'toast',
        message: 'Live collaboration stopped',
        detail: info.stage || 'collaboration client failure'
      }),
    onSaveNotify: handleSaveNotify,
    onOpDropped: handleOpDropped
  });
  eventBus.on('collab:peers-changed', ({ peers } = {}) => {
    peerCount = Array.isArray(peers) ? peers.length : 0;
  });
  // Section soft-locks: install the real editing guard while connected; mirror
  // the server's push-replaced lock table; tear the guard down on disconnect.
  eventBus.on('collab:connected', () => {
    lastSaveActivityAt = Date.now();
    const roomVersion = client.getCloudVersion();
    void setCloudVersionAndPersist(
      client.getProjectId(),
      Number.isInteger(roomVersion) ? roomVersion : 0
    );
    installCollabLocks({
      send: (action, sectionKey) => client.sendLock(action, sectionKey),
      getSelfConnId: () => client.getSelfConnId(),
      toast: showToast
    });
    updateLockState(client.getLocks());
  });
  eventBus.on('collab:editor-changed', ({ editorClaim, selfConnId } = {}) => {
    const isSelf = Boolean(editorClaim && editorClaim.holderConn === selfConnId);
    if (isSelf) {
      store.removeBlocker(WRITE_BLOCKER.SESSION_CLAIM);
      claimModal?.close();
      claimModal = null;
    } else {
      if (store.addBlocker(WRITE_BLOCKER.SESSION_CLAIM)) {
        store.discardUnsavedForBlocker(WRITE_BLOCKER.SESSION_CLAIM);
      }
      eventBus.emit('collab:editor-readonly', { editorClaim });
      if (editorClaim) showEditorClaimModal(editorClaim);
    }
  });
  eventBus.on('collab:lock-state', ({ locks } = {}) => {
    updateLockState(locks);
    eventBus.emit('collab:locks-changed', { locks });
  });
  eventBus.on('collab:disconnected', () => {
    peerCount = 0;
    uninstallCollabLocks();
    eventBus.emit('collab:locks-changed', { locks: [] });
  });
  return client;
}

// ── public API ──
export function configureCollabSession(nextHooks = {}) {
  hooks = { ...hooks, ...nextHooks };
  store.setIdentityRepairHistoryGuard?.((entry, direction) =>
    collabIdentityRepairHistoryGuard(entry, direction, entry.identityRepairContext || {})
  );
}

export async function maybeConnectCollab(currentUser) {
  const attempt = ++connectAttempt;
  const initialState = store.getState();
  const projectId = initialState?.projectId;
  const userSub = currentUser?.sub == null ? null : String(currentUser.sub);
  const sessionCanConnect =
    store.isWritable() ||
    store.getBlockers().every((blocker) => blocker === WRITE_BLOCKER.SESSION_CLAIM);
  if (!isRealUser(currentUser) || !sessionCanConnect || !projectId) {
    if (attempt === connectAttempt) await disconnectCollab({ preserveAttempt: true });
    return false;
  }
  // SB-4a: the gate no longer asks "is this project SHARED?" — it asks "is the
  // room open?". Membership was a product attribute standing in for the
  // invariant that actually matters (two writers on one project), which is why
  // a solo owner in two browsers got no coordination at all. With the flag on,
  // no membership round-trip happens; with it off, the pre-SB-4a check stands.
  const allowed =
    devFlag() || collabAllProjectsEnabled() || (await isProjectShared(currentUser, projectId));
  const liveState = store.getState();
  if (
    attempt !== connectAttempt ||
    liveState !== initialState ||
    liveState?.projectId !== projectId ||
    !(
      store.isWritable() ||
      store.getBlockers().every((blocker) => blocker === WRITE_BLOCKER.SESSION_CLAIM)
    )
  ) {
    return false;
  }
  if (!allowed) {
    await disconnectCollab({ preserveAttempt: true });
    return false;
  }
  if (String(currentUser?.sub ?? '') !== userSub) return false;
  const c = ensureClient();
  if ((c.isConnected() || c.isConnecting()) && c.getProjectId() === projectId) return true;
  if (c.getProjectId() && c.getProjectId() !== projectId) {
    await c.disconnect({ flush: false });
    if (attempt !== connectAttempt || store.getState()?.projectId !== projectId) return false;
  }
  c.connect(projectId);
  return true;
}

export function disconnectCollab(opts) {
  if (!opts?.preserveAttempt) connectAttempt += 1;
  peerCount = 0;
  return client ? client.disconnect(opts) : Promise.resolve(true);
}

export function collabConnected() {
  return !!client && client.isConnected();
}
export function collabSessionActive() {
  const projectId = store.getState()?.projectId;
  return !!client && client.isActive() && client.getProjectId() === projectId;
}
export function collabIsSaveLeader() {
  return !!client && client.isSaveLeader();
}
export function collabIsSynced() {
  return !!client && client.isSynced();
}

/**
 * Repair admission requires explicit namespace/sharing evidence. No session
 * by itself says nothing about a cloud project's absent/offline peers.
 */
export function collabIdentityRepairReadiness({
  projectId,
  cloudProject,
  sharingKnown = false,
  shared = null
} = {}) {
  const state = store.getState();
  if (!projectId || state?.projectId !== projectId) return { ok: false, reason: 'project-changed' };
  if (client?.getProjectId() === projectId && client.isActive())
    return client.getIdentityRepairReadiness();
  if (cloudProject === false && identityRepairNamespaceOf(state) === 'local')
    return { ok: true, projectId, roomEpoch: null, atSeq: null };
  if (
    cloudProject === true &&
    sharingKnown === true &&
    shared === false &&
    getProfile()?.features?.collabAllProjects === false
  ) {
    return { ok: true, projectId, roomEpoch: null, atSeq: null };
  }
  return { ok: false, reason: 'collab-status-unknown' };
}

/** Register through StateStore's history guard seam; call BEFORE any mutation. */
export function collabIdentityRepairHistoryGuard(entry, _direction, context = {}) {
  const patches = [...(entry?.forward || []), ...(entry?.reverse || [])];
  if (!patches.some((patch) => patch.path?.split('.').includes('editableIdRepairs')))
    return { ok: true };
  const readiness = collabIdentityRepairReadiness({
    projectId: store.getState()?.projectId,
    ...context
  });
  if (readiness.ok) return readiness;
  return {
    ...readiness,
    message:
      'Wait for this project’s live session to finish syncing before undoing or redoing element identity repair.'
  };
}

/**
 * Phase 11: true when we're connected, NOT the elected save leader, and the room
 * has seen no save activity past the stall threshold — the shell lets a stalled
 * follower self-promote for one save tick so a frozen leader can't wedge saves.
 */
export function collabSaveLeaderStalled() {
  return (
    collabConnected() &&
    client.isEditor() &&
    !collabIsSaveLeader() &&
    Date.now() - lastSaveActivityAt > SAVE_LEADER_STALL_MS
  );
}
export function collabPeerCount() {
  return collabConnected() ? peerCount : 0;
}

/**
 * Capture the exact room position represented by a cloud-save bundle. Call
 * synchronously immediately before the save code clones `state`.
 */
export function collabCaptureSaveCheckpoint(state) {
  if (!client || state?.projectId !== client.getProjectId()) return null;
  if (!client.isConnected()) {
    if (client.isActive()) throw new Error('Cannot cloud-save while collaboration is reconnecting');
    return null;
  }
  if (!client.isEditor()) {
    throw new Error('Cannot cloud-save from a read-only collaboration session');
  }
  if (!client.isSynced() || !state || store.getState() !== state) {
    throw new Error('Cannot cloud-save a collaboration state that is not at the room head');
  }
  return Object.freeze({
    projectId: client.getProjectId(),
    roomEpoch: client.getRoomEpoch(),
    atSeq: client.getRoomHead()
  });
}

export function collabNotifySaved(version, checkpoint) {
  if (!client || !client.isConnected() || !Number.isInteger(version) || !checkpoint) return;
  if (
    checkpoint.projectId !== client.getProjectId() ||
    checkpoint.roomEpoch !== client.getRoomEpoch() ||
    !Number.isInteger(checkpoint.atSeq)
  )
    return;
  client.sendSaved(version, checkpoint.atSeq);
}

export function collabFlushOutbox(capMs = 3000) {
  return client && client.isConnected() ? client.flushOutbox(capMs) : Promise.resolve(true);
}

/** Peer-lock treatment rows for the iframe (Phase 9), or [] when solo. */
export function collabLockTreatmentRows() {
  return collabConnected() ? peerLockTreatmentRows(store.getState()) : [];
}

/** Send a presence frame to the room (Phase 10); no-op when disconnected. */
export function collabSendPresence(frame) {
  if (client && client.isConnected()) client.sendPresence(frame);
}

export function collabTakeOverEditor() {
  recordSessionBoundaryMetric('takeover_banner_accepted');
  return client?.claimEditor('takeover') || false;
}

// Test/inspection seam.
export function _collabClient() {
  return client;
}
